/*
  Homepage sections.
  - <home-dots>: one dot per page of its track, synced with the scroll position, click to scroll.
  - Product tabs: switch the visible panel.
  - Press carousel: the slide closest to the start of the track is marked active (full ink).
*/
(() => {
  class HomeDots extends HTMLElement {
    connectedCallback() {
      this.track = document.getElementById(this.dataset.track);
      if (!this.track) return;
      this.build = this.build.bind(this);
      this.sync = this.sync.bind(this);
      this.track.addEventListener('scroll', () => requestAnimationFrame(this.sync), { passive: true });
      new ResizeObserver(this.build).observe(this.track);
    }

    pages() {
      // data-per="item": one dot per slide (press), instead of one per page of the track.
      if (this.dataset.per === 'item') return this.track.children.length;
      const { scrollWidth, clientWidth } = this.track;
      if (!clientWidth) return 1;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return Math.max(1, Math.ceil((scrollWidth + gap) / (clientWidth + gap) - 0.05));
    }

    build() {
      const n = this.pages();
      if (n < 2) return this.replaceChildren();
      if (this.childElementCount !== n) {
        this.replaceChildren(
          ...Array.from({ length: n }, (_, i) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.tabIndex = -1;
            b.addEventListener('click', () => {
              const left = this.dataset.per === 'item'
                ? this.track.children[i].offsetLeft - this.track.children[0].offsetLeft
                : i * (this.track.clientWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0));
              this.track.scrollTo({ left, behavior: 'smooth' });
            });
            return b;
          })
        );
      }
      this.sync();
    }

    sync() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const n = this.childElementCount;
      if (!n) return;
      const max = scrollWidth - clientWidth;
      const current = max <= 0 ? 0 : Math.round((scrollLeft / max) * (n - 1));
      [...this.children].forEach((b, i) => b.setAttribute('aria-current', String(i === current)));
      this.track.querySelectorAll('.home-press__slide').forEach((slide) => {
        const left = slide.offsetLeft - this.track.offsetLeft - scrollLeft;
        slide.classList.toggle('is-active', left > -slide.offsetWidth / 2 && left < slide.offsetWidth / 2);
      });
    }
  }
  if (!customElements.get('home-dots')) customElements.define('home-dots', HomeDots);

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('.home-tabs__tab');
    if (!tab) return;
    const list = tab.closest('.home-tabs');
    list.querySelectorAll('.home-tabs__tab').forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
    list.querySelectorAll('.home-tabs__panel').forEach((p) => {
      p.hidden = p.id !== tab.getAttribute('aria-controls');
      if (!p.hidden) p.querySelector('home-dots')?.build();
    });
  });

  // Carousels: click and drag with the mouse (touch and trackpads already scroll natively). Snap is turned off while
  // dragging and the track settles on the nearest item on release; a click right after a drag is swallowed so links
  // inside the cards don't open.
  let drag = null;

  // Snap comes back only once the settle animation is over, and never while a new drag is under way (a leftover
  // release from the previous drag would turn snap back on mid-drag and pull the track back to the same card).
  const releaseTimers = new WeakMap();
  const releaseLater = (track) => {
    clearTimeout(releaseTimers.get(track));
    releaseTimers.set(track, setTimeout(() => {
      if (drag?.track !== track) track.classList.remove('is-dragging');
    }, 600));
  };

  // mousedown's default (text selection, native image/link drag) is what used to break the second drag.
  document.addEventListener('mousedown', (event) => {
    if (event.button === 0 && event.target.closest('.home-track')) event.preventDefault();
  });

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const track = event.target.closest('.home-track');
    if (!track || track.scrollWidth <= track.clientWidth) return;
    clearTimeout(releaseTimers.get(track));
    // Stop a settle animation still running from the previous drag, so it doesn't fight this one.
    track.scrollTo({ left: track.scrollLeft, behavior: 'instant' });
    drag = { track, startX: event.clientX, startLeft: track.scrollLeft, moved: false };
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag) return;
    const dx = event.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(dx) < 5) return;
      drag.moved = true;
      drag.track.classList.add('is-dragging');
    }
    drag.track.scrollLeft = drag.startLeft - dx;
  });

  const endDrag = () => {
    if (!drag) return;
    const { track, moved } = drag;
    drag = null;
    if (!moved) return;
    const start = track.children[0]?.offsetLeft ?? 0;
    const items = [...track.children].filter((child) => child.offsetParent);
    const nearest = items.reduce((best, item) => {
      const left = item.offsetLeft - start;
      return Math.abs(left - track.scrollLeft) < Math.abs(best - track.scrollLeft) ? left : best;
    }, 0);
    track.scrollTo({ left: nearest, behavior: 'smooth' });
    releaseLater(track);
    // The click (if any) fires right after pointerup, before this timeout clears the guard.
    const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };
    track.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => track.removeEventListener('click', swallow, { capture: true }), 0);
  };

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  // Belt and braces for the native image / link drag.
  document.addEventListener('dragstart', (event) => {
    if (event.target.closest?.('.home-track')) event.preventDefault();
  });

  // Press: mark the first slide active before any scroll.
  document.querySelectorAll('.home-press__track').forEach((t) => t.querySelector('.home-press__slide')?.classList.add('is-active'));
})();

// UGC: tap to play / pause a muted inline video.
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-ugc-play]');
  const post = (button || event.target).closest('.home-ugc__post');
  const video = post?.querySelector('video');
  if (!video || !(button || event.target === video)) return;
  if (video.paused) {
    video.play();
    post.classList.add('is-playing');
  } else {
    video.pause();
    post.classList.remove('is-playing');
  }
});
